import React, { useMemo, useState } from "react";
import * as R from "remeda";

import type { DropdownComponentProps } from "../../components/common/Dropdown";
import { DropdownComponent } from "../../components/common/Dropdown";
import type { DropdownItemData } from "../../components/common/DropdownHelpers";
import {
    useClearDimensionValueOnDependencyChange,
    useGetDimensionDisable,
    useSetDimensionValue,
    useUpdateStatusOnChange,
} from "../../hooks/dimension/dimension.hooks";
import { useDimensionHydration } from "../../store/dimension.hydration";
import { DimensionStatus } from "../../store/dimension.status";
import type { DropdownDimensionValue } from "../../store/dimension.value";
import type {
    DimensionConfiguration,
    DimensionTemplate,
} from "../../types/dimensions";
import { buildRequestParameter } from "../../utils/app.utils";
import { filterItemByValueAndLabel } from "../common/DropdownHelpers";

import { BasicDimensionBox } from "./common/components";

export interface StaticDropdownDimensionConfiguration
    extends DimensionConfiguration {
    items: DropdownItemData[];
    showSearchInput?: boolean;
    additionalItemAttributes?: string[];
}

export interface StaticDropdownDimensionProps extends DimensionTemplate {
    configuration: StaticDropdownDimensionConfiguration;
}

export const StaticDropdownDimension: React.FunctionComponent<
    StaticDropdownDimensionProps
> = (props) => {
    const [filter, setFilter] = useState("");
    const { id, dependencies, configuration, optional, requestParameterMap } =
        props;
    const {
        label,
        tooltip,
        items,
        showSearchInput,
        additionalItemAttributes = [],
    } = configuration;

    const rpMap = useMemo(() => {
        const attributes = R.pipe(
            additionalItemAttributes,
            R.map((e) => ({ [e]: e })),
            R.mergeAll,
        );
        return (
            requestParameterMap || {
                value: `${id}_id`,
                label: `${id}_name`,
                ...attributes,
            }
        );
    }, []);

    const disabled = useGetDimensionDisable(dependencies);
    const [dimensionData, setDimensionData] =
        useSetDimensionValue<DropdownDimensionValue>(
            id,
            buildRequestParameter(rpMap),
        );

    useUpdateStatusOnChange<DropdownDimensionValue>(id, ({ value }) => {
        if (optional) return DimensionStatus.SUCCESS;
        if (value.length === 0) {
            return DimensionStatus.EMPTY;
        }
        return DimensionStatus.SUCCESS;
    });

    useClearDimensionValueOnDependencyChange(id, dependencies);

    const filteredItems = useMemo(
        () => filterItemByValueAndLabel(items, filter),
        [filter],
    );

    useDimensionHydration(id, dependencies, async (hydrationPayload) => {
        const payloadValues = {
            valueLookupKey: (() => {
                if (requestParameterMap) {
                    return requestParameterMap.value;
                } else {
                    return `${id}_id`;
                }
            })(),
            labelLookupKey: (() => {
                if (requestParameterMap) {
                    return requestParameterMap.label;
                } else {
                    return `${id}_name`;
                }
            })(),
        };

        // if value is set in payload use
        if (hydrationPayload[payloadValues.valueLookupKey]) {
            const item = items.find(
                (e) =>
                    e.value === hydrationPayload[payloadValues.valueLookupKey],
            );
            if (item) {
                setDimensionData({
                    ...item,
                    value: item.value,
                    label: item.label,
                });
            } else {
                throw new Error(
                    `Dropdown item with value ${hydrationPayload[payloadValues.valueLookupKey]} not found`,
                );
            }
        } else {
            throw new Error("could not locate value, label in payload");
        }
    });

    const dropdownProps: DropdownComponentProps = {
        dimension: id,
        tooltip,
        label,
        items: filteredItems,
        disabled,
        search: filter,
        setSearch: setFilter,
        selectedValue: dimensionData.value,
        showSearchInput,
        onChange: (v) => {
            const item: DropdownItemData | undefined = items.find(
                (i) => i.value === v,
            );
            if (item) {
                setDimensionData({
                    ...item,
                });
            }
        },
        optional,
    };
    return (
        <BasicDimensionBox>
            <DropdownComponent {...dropdownProps} />
        </BasicDimensionBox>
    );
};
